import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { MapPin, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import AddressMapPicker from './AddressMapPicker';

const CoverageModal = ({ open, onOpenChange }) => {
  const { user, refresh } = useAuth();
  const [address, setAddress] = useState(null);
  const [zones, setZones] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/coverage/zones').then((r) => setZones(r.data)).catch(() => setZones([]));
    setResult(null);
    setAddress(null); // reset search on every open
  }, [open]);

  const check = async (save = false) => {
    if (!address?.lat) {
      setResult({ ok: false, message: 'Selecciona una dirección en el mapa' });
      return;
    }
    setLoading(true);
    try {
      const r = await api.post('/coverage/check-location', {
        lat: address.lat,
        lng: address.lng,
        address: address.address,
        zip: address.zip,
        save: save && !!user,
      });
      if (r.data.covered) {
        setResult({ ok: true, message: 'Entregamos en tu dirección.' });
        if (save && user) await refresh();
      } else {
        const km = r.data.distance_meters ? (r.data.distance_meters / 1000).toFixed(1) : null;
        setResult({ ok: false, message: `Aún no llegamos a esta zona${km ? ` (más cercana a ${km} km)` : ''}` });
      }
    } catch { setResult({ ok: false, message: 'Error al verificar' }); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl bg-neutral-950 border-white/10 text-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl md:text-2xl flex items-center gap-2 text-white">
            <MapPin className="w-6 h-6 text-orange-500" /> Verificar cobertura
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Escribe tu dirección o ajusta el marcador en el mapa.
          </DialogDescription>
        </DialogHeader>

        <AddressMapPicker
          value={address}
          onChange={(v) => { setAddress(v); setResult(null); }}
          zones={zones}
          height={360}
        />

        <div className="flex justify-center">
          <Button onClick={() => check(false)} disabled={loading} className="w-full sm:w-auto sm:min-w-[220px] h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando...</> : 'Verificar'}
          </Button>
        </div>

        {result && (
          <div className={`rounded-xl px-4 py-3 flex items-start gap-2 ${result.ok ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-rose-500/15 text-rose-300 border border-rose-500/30'}`}>
            {result.ok ? <CheckCircle2 className="w-5 h-5 mt-0.5" /> : <XCircle className="w-5 h-5 mt-0.5" />}
            <div>
              <p className="font-semibold">{result.ok ? '¡Excelente! Tenemos cobertura' : 'Sin cobertura'}</p>
              <p className="text-sm opacity-90">{result.message}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CoverageModal;
